/**
 * PhaseTransition - the soft flash over an arrival that should startle.
 *
 * A change of look is a cross-fade of the whole screen (`hooks/useLivery`,
 * `utils/crossFade`). Two arrivals get one thing more: a fixed, click-through
 * veil that flashes once and is gone - red into Combat, yellow as Stealth's
 * ladder reaches Alarm. Which, is `engine/phase/transitions`; what each looks
 * like is the stylesheet. Soft on purpose: it rises and falls, and never
 * covers the screen outright.
 *
 * Nothing is drawn for a viewer whose screen holds still (`useHoldsStill`:
 * their own setting, which defaults to following the system). The stylesheet
 * does not ask the system itself.
 */
import { useEffect, useRef, useState } from 'react';
import styles from '../styles/PhaseTransition.module.css';
import { crossingBetween } from '../engine/phase/transitions';
import useHoldsStill from '../hooks/useHoldsStill';

/** How long a flash plays. The one place it is written: the stylesheet takes it from the inline style. */
const FLASH_MS = 700;

/**
 * The flash to play now, as `{ kind, key }`, cleared when its time is up.
 * The timer lives in a ref: a later change that starts no flash must leave a
 * playing one alone (gotchas, 2026-10-05). The key restarts the animation
 * when two follow closely.
 */
function useFlash(phase, rung) {
  const last = useRef({ phase, rung });
  const timer = useRef(null);
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    const kind = crossingBetween(last.current, { phase, rung })?.flash ?? null;
    last.current = { phase, rung };
    if (!kind) return;
    clearTimeout(timer.current);
    setFlash((prev) => ({ kind, key: (prev?.key ?? 0) + 1 }));
    timer.current = setTimeout(() => setFlash(null), FLASH_MS);
  }, [phase, rung]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return flash;
}

/**
 * @param {object} props
 * @param {import('../engine/phase/types').Phase | null} props.phase - the phase on this screen
 * @param {string | null} [props.rung] - Stealth's rung on this screen; null where there is no ladder
 */
export default function PhaseTransition({ phase, rung = null }) {
  const flash = useFlash(phase, rung);
  const still = useHoldsStill();
  if (!flash || still) return null;
  return (
    <div
      key={flash.key}
      className={styles.veil}
      style={{ animationDuration: FLASH_MS + 'ms' }}
      data-transition={flash.kind}
      data-testid="phase-transition"
      aria-hidden="true"
    />
  );
}
